import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Shield,
  Plus,
  X,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Layers,
} from "lucide-react";
import { apiService } from "../services/api";
import {
  ApplicationDependencyTrackLink,
  ApplicationFindingsResult,
  DtVersionSelection,
  LinkedDtProject,
  VulnerabilityFinding,
} from "../types";
import { dependencyTrackProjectUrl } from "../utils/env";
import { describeDtVersions } from "../utils/dtVersions";
import { useDtVersionSelection } from "../hooks/useDtVersionSelection";
import DtVersionSelectModal from "./DtVersionSelectModal";

interface ApplicationDependencyTrackPanelProps {
  applicationId: string;
}

const SEVERITY_STYLES: Record<string, string> = {
  CRITICAL:
    "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800",
  HIGH: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 border-orange-200 dark:border-orange-800",
  MEDIUM:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  LOW: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800",
  INFO: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600",
  UNASSIGNED:
    "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600",
};

function severityClass(sev: string): string {
  return (
    SEVERITY_STYLES[sev?.toUpperCase()] ?? SEVERITY_STYLES.UNASSIGNED ?? ""
  );
}

/** Build a deep-link URL for a vulnerability based on its source. */
function vulnUrl(vulnId: string, source?: string): string {
  const s = source?.toUpperCase();
  if (s === "GITHUB")
    return `https://github.com/advisories/${encodeURIComponent(vulnId)}`;
  if (s === "OSSINDEX")
    return `https://ossindex.sonatype.org/vulnerability/${encodeURIComponent(vulnId)}`;
  // NVD is the default — works for any CVE-* identifier.
  return `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(vulnId)}`;
}

// How many CVEs to show per page in the findings table.
const FINDINGS_PAGE_SIZE = 10;

export default function ApplicationDependencyTrackPanel({
  applicationId,
}: ApplicationDependencyTrackPanelProps) {
  const queryClient = useQueryClient();
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [projectUuid, setProjectUuid] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showFindings, setShowFindings] = useState(true);
  const [findingsPage, setFindingsPage] = useState(1);
  const [activeSeverities, setActiveSeverities] = useState<string[]>([
    "CRITICAL",
    "HIGH",
  ]);
  const [versionSelection, setVersionSelection] = useDtVersionSelection();
  const [showVersionModal, setShowVersionModal] = useState(false);

  const toggleSeverity = (sev: string) => {
    setActiveSeverities((prev) =>
      prev.includes(sev) ? prev.filter((s) => s !== sev) : [...prev, sev],
    );
    setFindingsPage(1);
  };

  const applyVersions = (selection: DtVersionSelection) => {
    setVersionSelection(selection);
    setFindingsPage(1);
  };

  const linksKey = ["app-dt-links", applicationId];
  const projectsKey = ["app-dt-projects", applicationId];
  const findingsKeyBase = ["app-dt-findings", applicationId];

  const { data: linksRes, isLoading: linksLoading } = useQuery(
    linksKey,
    () => apiService.getApplicationDependencyTrackLinks(applicationId),
    { enabled: !!applicationId },
  );
  const links: ApplicationDependencyTrackLink[] =
    (linksRes?.data as any)?.data ?? [];

  const { data: projectsRes } = useQuery(
    projectsKey,
    () => apiService.getApplicationDependencyTrackProjects(applicationId),
    { enabled: !!applicationId && links.length > 0 },
  );
  const projects: LinkedDtProject[] = (projectsRes?.data as any)?.data ?? [];

  const {
    data: findingsRes,
    isLoading: findingsLoading,
    isFetching: findingsFetching,
    refetch: refetchFindings,
  } = useQuery(
    [...findingsKeyBase, findingsPage, activeSeverities, versionSelection],
    () =>
      apiService.getApplicationDependencyTrackFindings(
        applicationId,
        findingsPage,
        FINDINGS_PAGE_SIZE,
        activeSeverities.length > 0 ? activeSeverities : undefined,
        versionSelection,
      ),
    {
      enabled: !!applicationId && links.length > 0,
      keepPreviousData: true,
    },
  );
  const findings: ApplicationFindingsResult | null =
    (findingsRes?.data as any)?.data ?? null;

  const linkMutation = useMutation(
    (uuid: string) =>
      apiService.linkApplicationDependencyTrackProject(applicationId, uuid),
    {
      onSuccess: () => {
        setProjectUuid("");
        setShowLinkForm(false);
        setLinkError(null);
        setFindingsPage(1);
        queryClient.invalidateQueries(linksKey);
        queryClient.invalidateQueries(projectsKey);
        queryClient.invalidateQueries(findingsKeyBase);
      },
      onError: (err: any) => {
        setLinkError(
          err?.response?.data?.error ||
            "Failed to link Dependency-Track project",
        );
      },
    },
  );

  const unlinkMutation = useMutation(
    (linkId: string) =>
      apiService.unlinkApplicationDependencyTrackProject(applicationId, linkId),
    {
      onSuccess: () => {
        setFindingsPage(1);
        queryClient.invalidateQueries(linksKey);
        queryClient.invalidateQueries(projectsKey);
        queryClient.invalidateQueries(findingsKeyBase);
      },
    },
  );

  const handleLink = () => {
    const uuid = projectUuid.trim();
    if (!uuid) {
      setLinkError("Enter a Dependency-Track project UUID");
      return;
    }
    linkMutation.mutate(uuid);
  };

  const pageFindings: VulnerabilityFinding[] = findings?.findings ?? [];
  const projectErrors = findings?.errors ?? [];
  const totalPages = findings?.totalPages ?? 1;
  const totalFindings = findings?.total ?? 0;
  const pageStart =
    totalFindings === 0 ? 0 : (findingsPage - 1) * FINDINGS_PAGE_SIZE + 1;
  const pageEnd = Math.min(findingsPage * FINDINGS_PAGE_SIZE, totalFindings);
  // `total` is post-filter; the severity counts are not.
  const allFindingsCount = findings
    ? findings.critical +
      findings.high +
      findings.medium +
      findings.low +
      findings.unassigned
    : 0;

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Shield className="h-5 w-5" /> Dependency-Track
          <span className="text-sm font-normal text-gray-500">
            ({links.length})
          </span>
        </h2>
        <button
          onClick={() => {
            setShowLinkForm((v) => !v);
            setLinkError(null);
          }}
          className="btn btn-primary text-sm flex items-center gap-1"
        >
          <Plus className="h-3.5 w-3.5" /> Link Project
        </button>
      </div>

      {/* Link form */}
      {showLinkForm && (
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dependency-Track project UUID
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. eb3b146c-12b8-4399-8e86-53623ff88ddd"
              value={projectUuid}
              onChange={(e) => setProjectUuid(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLink()}
              className="input flex-1 text-sm font-mono"
            />
            <button
              onClick={handleLink}
              disabled={linkMutation.isLoading}
              className="btn btn-primary text-sm whitespace-nowrap"
            >
              {linkMutation.isLoading ? "Linking…" : "Link"}
            </button>
            <button
              onClick={() => {
                setShowLinkForm(false);
                setProjectUuid("");
                setLinkError(null);
              }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Find this UUID in Dependency-Track on the project page (Project
            Details → Object Identifier), or in the project URL.
          </p>
          {linkError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              {linkError}
            </p>
          )}
        </div>
      )}

      {/* Linked projects */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {linksLoading ? (
          <div className="px-6 py-8 flex justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
          </div>
        ) : links.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-gray-500">
            No Dependency-Track projects linked yet. Link one to fetch and
            display its CVEs here.
          </p>
        ) : (
          links.map((link) => (
            <div
              key={link.id}
              className="px-6 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {link.dtProjectName || "Unknown project"}
                  </span>
                  <a
                    href={dependencyTrackProjectUrl(link.dtProjectUuid)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600"
                    title="Open in Dependency-Track"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
                <span className="text-xs font-mono text-gray-400">
                  {link.dtProjectUuid}
                </span>
              </div>
              <button
                onClick={() => unlinkMutation.mutate(link.id)}
                className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                title="Unlink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Findings (CVEs) */}
      {links.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="px-6 py-3 flex items-center justify-between">
            <button
              onClick={() => setShowFindings((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              {showFindings ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Vulnerabilities
              {findings && (
                <span className="text-xs font-normal text-gray-500">
                  ({allFindingsCount})
                </span>
              )}
            </button>
            <div className="flex items-center gap-3">
              {projects.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowVersionModal(true)}
                  className="btn btn-secondary text-xs px-2 py-1 flex items-center gap-1"
                  title="Choose the version shown for each project"
                >
                  <Layers className="h-3 w-3" />
                  {describeDtVersions(versionSelection, projects)}
                </button>
              )}
              {findings && (
                <div className="flex items-center gap-1.5">
                  {(
                    [
                      ["CRITICAL", findings.critical],
                      ["HIGH", findings.high],
                      ["MEDIUM", findings.medium],
                      ["LOW", findings.low],
                    ] as const
                  )
                    .filter(([, n]) => n > 0)
                    .map(([sev, n]) => (
                      <button
                        type="button"
                        key={sev}
                        onClick={() => toggleSeverity(sev)}
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity ${severityClass(
                          sev,
                        )} ${activeSeverities.length > 0 && !activeSeverities.includes(sev) ? "opacity-40" : ""}`}
                        title={
                          activeSeverities.includes(sev)
                            ? `Hide ${sev.toLowerCase()} findings`
                            : `Show ${sev.toLowerCase()} findings`
                        }
                      >
                        {n} {sev.charAt(0) + sev.slice(1).toLowerCase()}
                      </button>
                    ))}
                </div>
              )}
              <button
                onClick={() => refetchFindings()}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Refresh findings"
              >
                <RefreshCw
                  className={`h-4 w-4 ${findingsFetching ? "animate-spin" : ""}`}
                />
              </button>
            </div>
          </div>

          {showFindings && (
            <div className="px-6 pb-4">
              {projectErrors.length > 0 && (
                <div className="mb-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Could not load findings for{" "}
                    {projectErrors.map((p) => p.dtProjectName).join(", ")}.
                  </span>
                </div>
              )}

              {findingsLoading && !findings ? (
                <div className="py-6 flex justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" />
                </div>
              ) : pageFindings.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  {projectErrors.length > 0
                    ? "No findings could be loaded."
                    : allFindingsCount > 0
                      ? "No findings match the selected severity."
                      : "No vulnerabilities found in the linked project(s)."}
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="py-2 pr-4 font-medium">
                            Dependency-Track Project
                          </th>
                          <th className="py-2 pr-4 font-medium">Version</th>
                          <th className="py-2 pr-4 font-medium">
                            Vulnerability
                          </th>
                          <th className="py-2 pr-4 font-medium">Severity</th>
                          <th className="py-2 pr-4 font-medium">CVSS</th>
                          <th className="py-2 pr-4 font-medium">Component</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {pageFindings.map((finding, idx) => (
                          <tr
                            key={`${finding.vulnId}-${finding.componentPurl}-${idx}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <td className="py-2 pr-4">
                              {finding.projectUuid ? (
                                <a
                                  href={dependencyTrackProjectUrl(
                                    finding.projectUuid,
                                  )}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                                >
                                  {finding.projectName || finding.projectUuid}
                                </a>
                              ) : (
                                <span className="text-xs text-gray-700 dark:text-gray-300">
                                  {finding.projectName || "—"}
                                </span>
                              )}
                            </td>
                            <td className="py-2 pr-4 font-mono text-xs text-gray-700 dark:text-gray-300">
                              {finding.projectVersion || "—"}
                            </td>
                            <td className="py-2 pr-4">
                              <a
                                href={vulnUrl(finding.vulnId, finding.source)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 hover:underline"
                              >
                                {finding.vulnId}
                              </a>
                              {finding.source && (
                                <span className="ml-1 text-xs text-gray-400">
                                  {finding.source}
                                </span>
                              )}
                              {finding.title && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                                  {finding.title}
                                </div>
                              )}
                            </td>
                            <td className="py-2 pr-4">
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${severityClass(
                                  finding.severity,
                                )}`}
                              >
                                {finding.severity || "UNKNOWN"}
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                              {finding.cvssScore > 0
                                ? finding.cvssScore.toFixed(1)
                                : "—"}
                            </td>
                            <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                              <span className="font-mono text-xs">
                                {finding.componentName}
                                {finding.componentVersion
                                  ? `@${finding.componentVersion}`
                                  : ""}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>
                      Showing {pageStart}–{pageEnd} of {totalFindings}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setFindingsPage((p) => Math.max(1, p - 1))
                        }
                        disabled={findingsPage <= 1 || findingsFetching}
                        className="btn btn-secondary px-2 py-1 flex items-center gap-1 disabled:opacity-40"
                        title="Previous page"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Prev
                      </button>
                      <span className="tabular-nums">
                        Page {findingsPage} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setFindingsPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={
                          findingsPage >= totalPages || findingsFetching
                        }
                        className="btn btn-secondary px-2 py-1 flex items-center gap-1 disabled:opacity-40"
                        title="Next page"
                      >
                        Next <ChevronRight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {showVersionModal && (
        <DtVersionSelectModal
          projects={projects}
          selection={versionSelection}
          onApply={applyVersions}
          onClose={() => setShowVersionModal(false)}
        />
      )}
    </div>
  );
}
